/**
 * 用户权限配置文件
 * 根据用户名配置可访问的路由
 */

// 定义路由权限映射关系
// key: 用户名
// value: 允许访问的路由name数组
export const userPermissions = {
    // job 账号只能访问招聘管理
    job: ['recruitment', 'recruitmentList'],

    // admin 账号拥有所有权限
    admin: ['news', 'newsType', 'newsList', 'createNews',
        'case', 'caseType', 'caseList', 'createcase',
        'Advertising', 'firstPage_banner', 'createAdBanner', 'editAdBanner', 'routeList',
        'company', 'contactUs', 'createCompany',
        'product', 'productCase', 'createproduct',
        'recruitment', 'recruitmentList',
        'system', 'config'],

    // 可以继续添加其他账号的权限配置
    // editor: ['news', 'newsType', 'newsList', 'createNews'],
    // designer: ['case', 'caseType', 'caseList', 'createcase'],
}

// 定义用户默认首页路由
// key: 用户名
// value: 默认跳转的路由路径
export const userDefaultRoutes = {
    job: '/recruitment/list',
    admin: '/news/type',

    // 可以继续添加其他账号的默认路由
    // editor: '/news/list',
    // designer: '/case/list',
}

/**
 * 根据用户名获取允许访问的路由名称列表
 * @param {string} username - 用户名
 * @returns {Array} 允许访问的路由名称数组
 */
export function getPermissionsByUsername(username) {
    return userPermissions[username] || []
}

/**
 * 检查用户是否有权限访问某个路由
 * @param {string} username - 用户名
 * @param {string} routeName - 路由名称
 * @returns {boolean} 是否有权限
 */
export function hasPermission(username, routeName) {
    const permissions = getPermissionsByUsername(username)
    // 如果没有配置权限，默认不允许访问（除了登录页等公共页面）
    if (!permissions || permissions.length === 0) {
        return false
    }
    return permissions.includes(routeName)
}

/**
 * 过滤路由，只返回用户有权限访问的路由
 * @param {Array} routes - 完整路由配置
 * @param {string} username - 用户名
 * @returns {Array} 过滤后的路由配置
 */
export function filterRoutes(routes, username) {
    const permissions = getPermissionsByUsername(username)

    // 如果没有配置权限，返回空数组
    if (!permissions || permissions.length === 0) {
        return []
    }

    // 递归过滤路由
    const filterRoute = (routeList) => {
        return routeList.filter(route => {
            // 跳过 hidden 为 true 的路由（这些路由虽然不在菜单显示，但可能需要保留用于编程式导航）
            // 实际上这里我们不完全跳过，而是根据父路由权限来决定

            // 如果路由没有 name，通常是容器路由或特殊路由，检查其子路由
            if (!route.name) {
                if (route.children && route.children.length > 0) {
                    // 深拷贝路由对象，避免修改原始配置
                    const routeCopy = { ...route }
                    routeCopy.children = filterRoute(route.children)
                    // 如果过滤后还有子路由，保留该路由
                    return routeCopy.children.length > 0
                }
                // 没有 name 也没有 children，直接跳过
                return false
            }

            // 检查当前路由是否有权限
            const hasRoutePermission = permissions.includes(route.name)

            if (!hasRoutePermission) {
                return false
            }

            // 如果有权限且有子路由，过滤子路由
            if (route.children && route.children.length > 0) {
                const routeCopy = { ...route }
                routeCopy.children = filterRoute(route.children)
                // 即使子路由被过滤完了，只要父路由有权限，仍然保留
                return true
            }

            return true
        }).map(route => {
            // 深拷贝，避免修改原始路由配置
            const routeCopy = { ...route }
            if (route.children && route.children.length > 0) {
                routeCopy.children = filterRoute(route.children)
            }
            return routeCopy
        })
    }

    return filterRoute(routes)
}

/**
 * 获取用户的默认首页路由
 * @param {string} username - 用户名
 * @returns {string} 默认路由路径
 */
export function getDefaultRoute(username) {
    // 优先使用配置的默认路由
    if (userDefaultRoutes[username]) {
        return userDefaultRoutes[username]
    }

    // 如果没有配置，返回第一个有权限的路由路径
    const permissions = getPermissionsByUsername(username)
    if (!permissions || permissions.length === 0) {
        return '/login' // 没有权限，返回登录页
    }

    // 根据权限列表推断默认路由
    // 优先级：招聘 > 新闻 > 案例 > 广告 > 其他
    if (permissions.includes('recruitment')) {
        return '/recruitment/list'
    } else if (permissions.includes('news')) {
        return '/news/type'
    } else if (permissions.includes('case')) {
        return '/case/list'
    } else if (permissions.includes('Advertising')) {
        return '/Advertising/firstPage_banner'
    } else if (permissions.includes('company')) {
        return '/company/contactUs'
    }

    // 兜底：返回根路径
    return '/'
}
